// Upload pane: two ways in, as tabs.
// - Camera: live getUserMedia camera, one-tap shutter capturing a JPEG from a
//   canvas. Every shot is MANDATORY crop+rotate'd first (CropModal) — nothing is
//   uploaded until the operator confirms the frame.
// - Upload files: drag image files onto the drop zone or pick them from the
//   computer (FileDropZone). Each is converted to JPEG (imageFile.ts) and goes
//   straight in — no crop step — through a small queue (MAX_PARALLEL_FILES).
// Either way the pipeline runs ONCE per image: POST /uploads -> PUT presigned ->
// (artwork only) POST .../enrich + poll pending -> needs_review. A "kind" selector
// at the top picks the art-lookup happy path vs a "general" item that skips
// lookup (the server mints it straight to needs_review; no enrich). A shared
// thumbnail strip gives retake/re-upload/delete + a running count.
// CONTRACTS §10, DESIGN §9.

import { useCallback, useEffect, useRef, useState } from "react";
import { captureJpegFromVideo, useCamera } from "../camera";
import {
  ApiError,
  createUpload,
  deleteArtifact,
  enrich,
  getArtifact,
  pollUntilSettled,
  putImage,
} from "../api";
import type { Artifact, ArtifactKind, ArtifactStatus } from "../types";
import { CropModal } from "../components/CropModal";
import { FileDropZone } from "../components/FileDropZone";
import {
  FORMAT_LABEL,
  prepareImageFile,
  sniffImageFormat,
  type ImageFormat,
} from "../imageFile";

type ShotState = "queued" | "preparing" | "uploading" | "enriching" | "settled" | "error";

type ShotSource =
  // A confirmed camera crop, kept so we can retry/retake the SAME frame.
  | { origin: "camera"; jpeg: Blob }
  // A file from the computer: converted to JPEG when its turn comes (and again on
  // a re-upload), so a big drop never holds dozens of decoded photos in memory.
  | { origin: "file"; file: File; format: ImageFormat };

interface Shot {
  localId: string; // client-only id for the strip
  previewUrl?: string; // object URL for the thumbnail (a file gets one once converted)
  source: ShotSource;
  kind: ArtifactKind; // class chosen at capture/drop (drives enrich vs skip)
  artifactId?: string;
  status?: ArtifactStatus; // last polled artifact status
  state: ShotState;
  error?: string;
}

type InputMode = "camera" | "files";

let shotSeq = 0;

// Dropped files run the pipeline a few at a time, so a 50-image drop doesn't fire
// 50 enrichments (Gemini + SerpApi) in one burst or decode 50 photos at once.
const MAX_PARALLEL_FILES = 3;

// Remember Camera vs Upload files per device (a laptop shouldn't light its webcam
// on every visit). Best-effort: storage can be unavailable (private mode).
const MODE_KEY = "uploadInputMode";

function loadMode(): InputMode {
  try {
    return localStorage.getItem(MODE_KEY) === "files" ? "files" : "camera";
  } catch {
    return "camera";
  }
}

const fileKey = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;

function listNames(names: string[]): string {
  const shown = names.slice(0, 3).join(", ");
  return names.length > 3 ? `${shown} +${names.length - 3} more` : shown;
}

export interface UploadPaneProps {
  /**
   * False while another pane is showing. The pane stays MOUNTED (just hidden) so
   * a queue of dropped files keeps uploading while you check Review; only the
   * camera is released.
   */
  active: boolean;
  /** Bubble newly-settled artifacts into the global store + counts. */
  onArtifactSettled: (a: Artifact) => void;
  /** A strip delete/retake discarded this server record: drop it from the store too. */
  onArtifactRemoved: (id: string) => void;
}

export function UploadPane({ active, onArtifactSettled, onArtifactRemoved }: UploadPaneProps) {
  const [mode, setModeState] = useState<InputMode>(loadMode);
  const camera = useCamera(active && mode === "camera");
  const [shots, setShots] = useState<Shot[]>([]);
  // The item class for the NEXT capture/upload. Defaults to the art-lookup happy path.
  const [kind, setKind] = useState<ArtifactKind>("artwork");
  // A just-captured frame awaiting the MANDATORY crop+rotate. Nothing is uploaded
  // until the operator confirms it in the CropModal; cancel discards it.
  const [pendingCapture, setPendingCapture] = useState<Blob | null>(null);
  // Feedback on the last drop/pick (unsupported or already-added files).
  const [notice, setNotice] = useState<string | null>(null);
  const captureLock = useRef(false);
  // One AbortController per in-flight shot pipeline so delete/retake/unmount can
  // cancel its long poll (it would otherwise keep hitting the API for ~100s).
  // Presence here also marks a file as running, for the queue below.
  const controllers = useRef<Map<string, AbortController>>(new Map());
  const shotsRef = useRef<Shot[]>([]);
  shotsRef.current = shots;

  const setMode = useCallback((next: InputMode) => {
    setModeState(next);
    try {
      localStorage.setItem(MODE_KEY, next);
    } catch {
      // not remembered — fine
    }
  }, []);

  const abortShot = useCallback((localId: string) => {
    const c = controllers.current.get(localId);
    if (c) {
      c.abort();
      controllers.current.delete(localId);
    }
  }, []);

  // On unmount: abort every in-flight poll and revoke object URLs.
  useEffect(() => {
    return () => {
      for (const s of shotsRef.current) if (s.previewUrl) URL.revokeObjectURL(s.previewUrl);
      for (const c of controllers.current.values()) c.abort();
      controllers.current.clear();
    };
  }, []);

  const patchShot = useCallback((localId: string, patch: Partial<Shot>) => {
    setShots((prev) => prev.map((s) => (s.localId === localId ? { ...s, ...patch } : s)));
  }, []);

  // Best-effort server delete; drop it from the app store either way so Review
  // never shows a record the strip has thrown away.
  const discardArtifact = useCallback(
    (id: string) => {
      void deleteArtifact(id).catch(() => {});
      onArtifactRemoved(id);
    },
    [onArtifactRemoved],
  );

  /**
   * Run the upload pipeline for one shot. A camera shot is already a cropped+
   * rotated JPEG; a file is first converted to one (prepareImageFile). Then:
   * - kind="artwork": POST /uploads -> PUT -> POST .../enrich -> poll to settle.
   * - kind="general": POST /uploads {kind:"general"} -> PUT -> GET the artifact
   *   ONCE (the server already minted it needs_review). NO enrich, NO polling.
   */
  const runPipeline = useCallback(
    async (localId: string, source: ShotSource, shotKind: ArtifactKind) => {
      abortShot(localId); // cancel any prior attempt for this shot first
      const controller = new AbortController();
      controllers.current.set(localId, controller);
      const { signal } = controller;
      patchShot(localId, {
        state: source.origin === "file" ? "preparing" : "uploading",
        error: undefined,
      });
      try {
        let jpeg: Blob;
        if (source.origin === "file") {
          const prepared = await prepareImageFile(source.file, source.format);
          if (signal.aborted) return;
          jpeg = prepared.jpeg;
          const hasPreview = shotsRef.current.some((s) => s.localId === localId && s.previewUrl);
          patchShot(localId, {
            state: "uploading",
            ...(hasPreview ? {} : { previewUrl: URL.createObjectURL(prepared.preview) }),
          });
        } else {
          jpeg = source.jpeg;
        }

        const { id, uploadUrl } = await createUpload(
          shotKind === "general"
            ? { contentType: "image/jpeg", kind: "general" }
            : { contentType: "image/jpeg" },
        );
        // Deleted/retaken while the record was being minted: don't strand it as a
        // stuck "pending" item. Otherwise record the id NOW so a delete/retake
        // during the PUT discards it.
        if (signal.aborted) {
          discardArtifact(id);
          return;
        }
        patchShot(localId, { artifactId: id });
        await putImage(uploadUrl, jpeg);
        if (signal.aborted) return;

        if (shotKind === "general") {
          // No online lookup: the record is already needs_review. Fetch it once
          // and surface it — no enrich, no long poll.
          const artifact = await getArtifact(id);
          if (signal.aborted) return;
          patchShot(localId, { status: artifact.status, state: "settled" });
          onArtifactSettled(artifact);
          return;
        }

        patchShot(localId, { state: "enriching" });
        await enrich(id);
        const settled = await pollUntilSettled(id, {
          signal,
          onTick: (a) => patchShot(localId, { status: a.status }),
        });
        // A still-pending result means enrich timed out or failed (status stays
        // pending + enrichmentError) — that is NOT "settled". Mark it error so it
        // never shows a misleading green "done", and surface where to fix it.
        if (settled.status === "pending") {
          patchShot(localId, {
            status: settled.status,
            state: "error",
            error:
              settled.enrichmentError ||
              "Still processing — check the Review tab’s “Needs attention” list or retry.",
          });
        } else {
          patchShot(localId, { status: settled.status, state: "settled" });
        }
        // Bubble into the store either way, so a stuck item surfaces in Review.
        onArtifactSettled(settled);
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return; // deleted/retaken
        const msg =
          err instanceof ApiError
            ? `${err.message}`
            : err instanceof Error
              ? err.message
              : "Upload failed";
        patchShot(localId, { state: "error", error: msg });
      } finally {
        // Only clear OUR controller: a retake may already have installed a new one.
        if (controllers.current.get(localId) === controller) controllers.current.delete(localId);
      }
    },
    [abortShot, discardArtifact, onArtifactSettled, patchShot],
  );

  // File queue: start queued files as slots free up, oldest first (the strip is
  // newest-first, so that's from the end) — files go up in the order given.
  useEffect(() => {
    const running = shots.filter(
      (s) => s.source.origin === "file" && controllers.current.has(s.localId),
    ).length;
    const next = shots
      .filter((s) => s.state === "queued" && !controllers.current.has(s.localId))
      .reverse()
      .slice(0, Math.max(0, MAX_PARALLEL_FILES - running));
    for (const s of next) void runPipeline(s.localId, s.source, s.kind);
  }, [shots, runPipeline]);

  // Until a file's bytes reach S3 the server knows nothing about it, so closing or
  // reloading the tab would silently drop the rest of a drop. (Once enrich has
  // been triggered the server finishes on its own.)
  const unsent = shots.some(
    (s) => s.state === "queued" || s.state === "preparing" || s.state === "uploading",
  );
  useEffect(() => {
    if (!unsent) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ""; // older Safari only prompts when this is set
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unsent]);

  const capture = useCallback(async () => {
    if (captureLock.current) return;
    const video = camera.videoRef.current;
    if (!video) return;
    captureLock.current = true;
    try {
      const jpeg = await captureJpegFromVideo(video);
      if (!jpeg) return;
      // Do NOT upload yet: open the MANDATORY crop+rotate. Only a confirmed crop
      // creates a shot and runs the pipeline (so enrich fires once, on the final
      // framed image). Cancel discards this frame — nothing is uploaded.
      setPendingCapture(jpeg);
    } finally {
      captureLock.current = false;
    }
  }, [camera]);

  // Operator confirmed the crop+rotate: NOW create the shot and run the pipeline
  // once on the final blob, tagged with the kind chosen at capture time.
  const onCropConfirm = useCallback(
    (blob: Blob) => {
      setPendingCapture(null);
      const shotKind = kind; // snapshot — the selector may change before it settles
      const localId = `shot-${++shotSeq}`;
      const previewUrl = URL.createObjectURL(blob);
      const source: ShotSource = { origin: "camera", jpeg: blob };
      const shot: Shot = { localId, previewUrl, source, kind: shotKind, state: "uploading" };
      setShots((prev) => [shot, ...prev]);
      void runPipeline(localId, source, shotKind);
    },
    [kind, runPipeline],
  );

  // Operator backed out of the crop: discard the captured frame entirely.
  const onCropCancel = useCallback(() => setPendingCapture(null), []);

  // Files dropped or picked: keep the supported images (by content, not name),
  // skip ones already in the strip, and queue the rest under the current kind.
  const addFiles = useCallback(
    async (files: File[]) => {
      const fileKind = kind; // snapshot, like a capture
      const known = new Set(
        shotsRef.current.flatMap((s) => (s.source.origin === "file" ? [fileKey(s.source.file)] : [])),
      );
      const added: Shot[] = [];
      const unsupported: string[] = [];
      const repeats: string[] = [];
      for (const file of files) {
        const format = await sniffImageFormat(file).catch(() => null); // e.g. a dropped folder
        if (!format) {
          unsupported.push(file.name);
        } else if (known.has(fileKey(file))) {
          repeats.push(file.name);
        } else {
          known.add(fileKey(file));
          const source: ShotSource = { origin: "file", file, format };
          added.push({ localId: `shot-${++shotSeq}`, source, kind: fileKind, state: "queued" });
        }
      }
      // Newest-first strip, like captures; the queue starts from the oldest end,
      // so the files still go up in the order they were given.
      const newestFirst = added.reverse();
      if (newestFirst.length) setShots((prev) => [...newestFirst, ...prev]);
      const notes: string[] = [];
      if (unsupported.length) notes.push(`Skipped ${listNames(unsupported)} — not a supported image.`);
      if (repeats.length) notes.push(`Already added: ${listNames(repeats)}.`);
      setNotice(notes.length ? notes.join(" ") : null);
    },
    [kind],
  );

  const retake = useCallback(
    (shot: Shot) => {
      // Re-run the pipeline for the same frame/file. Abort any in-flight attempt
      // and discard the prior server record FIRST — regardless of state — so a
      // half-finished or still-polling record can't linger as a duplicate.
      abortShot(shot.localId);
      if (shot.artifactId) discardArtifact(shot.artifactId);
      patchShot(shot.localId, { artifactId: undefined, status: undefined, error: undefined });
      // A file waits its turn in the queue; a camera frame re-runs right away.
      if (shot.source.origin === "file") patchShot(shot.localId, { state: "queued" });
      else void runPipeline(shot.localId, shot.source, shot.kind);
    },
    [abortShot, discardArtifact, patchShot, runPipeline],
  );

  const remove = useCallback(
    (shot: Shot) => {
      // Abort any in-flight poll, then best-effort delete the server record +
      // local strip entry.
      abortShot(shot.localId);
      if (shot.artifactId) discardArtifact(shot.artifactId);
      if (shot.previewUrl) URL.revokeObjectURL(shot.previewUrl);
      setShots((prev) => prev.filter((s) => s.localId !== shot.localId));
    },
    [abortShot, discardArtifact],
  );

  const settledCount = shots.filter((s) => s.state === "settled").length;
  const queuedCount = shots.filter((s) => s.state === "queued").length;

  return (
    <div className="pane upload-pane" hidden={!active}>
      <div className="upload-controls">
        <div className="segmented" role="tablist" aria-label="How to add images">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "camera"}
            className={`segmented__btn${mode === "camera" ? " segmented__btn--active" : ""}`}
            onClick={() => setMode("camera")}
          >
            Camera
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "files"}
            className={`segmented__btn${mode === "files" ? " segmented__btn--active" : ""}`}
            onClick={() => setMode("files")}
          >
            Upload files
          </button>
        </div>

        <div className="upload-controls__kind">
          <span className="strip-header__sub">
            What are you {mode === "camera" ? "capturing" : "uploading"}?
          </span>
          <div
            className="segmented"
            role="radiogroup"
            aria-label={`Item type for the next ${mode === "camera" ? "capture" : "upload"}`}
          >
            <button
              type="button"
              role="radio"
              aria-checked={kind === "artwork"}
              className={`segmented__btn${kind === "artwork" ? " segmented__btn--active" : ""}`}
              onClick={() => setKind("artwork")}
            >
              Artwork — look up online
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={kind === "general"}
              className={`segmented__btn${kind === "general" ? " segmented__btn--active" : ""}`}
              onClick={() => setKind("general")}
            >
              General item — skip lookup
            </button>
          </div>
        </div>
      </div>

      {mode === "camera" ? (
        <div className="camera-stage">
          <video ref={camera.videoRef} className="camera-video" playsInline muted />
          {camera.status !== "ready" && (
            <div className="camera-overlay">
              {camera.status === "error" ? (
                <>
                  <p>{camera.error ?? "Camera unavailable"}</p>
                  <button type="button" className="btn" onClick={() => void camera.start()}>
                    Retry camera
                  </button>
                </>
              ) : (
                <p>Starting camera…</p>
              )}
            </div>
          )}
          <button
            type="button"
            className="shutter"
            aria-label="Capture photo"
            disabled={camera.status !== "ready"}
            onClick={() => void capture()}
          >
            <span className="shutter__ring" />
          </button>
        </div>
      ) : (
        <>
          <FileDropZone onFiles={(files) => void addFiles(files)} />
          {notice && (
            <div className="drop-notice" role="status">
              <span>{notice}</span>
              <button
                type="button"
                className="btn btn--tiny btn--ghost"
                aria-label="Dismiss"
                onClick={() => setNotice(null)}
              >
                ✕
              </button>
            </div>
          )}
        </>
      )}

      <div className="strip-header">
        <strong>{shots.length}</strong> added this session
        {shots.length > 0 && (
          <span className="strip-header__sub">
            {" "}
            · {settledCount} reached review{queuedCount > 0 && ` · ${queuedCount} queued`}
          </span>
        )}
      </div>

      <ul className="thumb-strip">
        {shots.map((shot) => {
          const file = shot.source.origin === "file" ? shot.source : null;
          return (
            <li key={shot.localId} className={`thumb thumb--${shot.state}`}>
              {shot.previewUrl ? (
                <img
                  className="thumb__img"
                  src={shot.previewUrl}
                  alt={file ? file.file.name : "captured frame"}
                />
              ) : (
                <div className="thumb__img thumb__img--placeholder">
                  {file && FORMAT_LABEL[file.format]}
                </div>
              )}
              {file && (
                <div className="thumb__name" title={file.file.name}>
                  {file.file.name}
                </div>
              )}
              <div className="thumb__status">
                <ShotStatusLabel shot={shot} />
              </div>
              <div className="thumb__actions">
                {file ? (
                  <button
                    type="button"
                    className="btn btn--tiny"
                    disabled={shot.state === "queued"}
                    onClick={() => retake(shot)}
                    title="Upload this file again and redo the lookup (replaces the earlier upload)"
                  >
                    Retry
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn--tiny"
                    onClick={() => retake(shot)}
                    title="Re-upload this frame"
                  >
                    Retake
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn--tiny btn--danger"
                  onClick={() => remove(shot)}
                  title="Delete"
                >
                  Delete
                </button>
              </div>
            </li>
          );
        })}
        {shots.length === 0 && (
          <li className="thumb-strip__empty">
            {mode === "camera"
              ? "Tap the shutter to capture a binder page."
              : "Drop images above, or choose them from your computer."}
          </li>
        )}
      </ul>

      {pendingCapture && (
        <CropModal
          source={pendingCapture}
          title="Crop & rotate"
          confirmLabel={kind === "general" ? "Use photo" : "Use & look up"}
          onCancel={onCropCancel}
          onConfirm={onCropConfirm}
        />
      )}
    </div>
  );
}

function ShotStatusLabel({ shot }: { shot: Shot }) {
  if (shot.state === "error") {
    return <span className="status status--error" title={shot.error}>error · {shot.error}</span>;
  }
  if (shot.state === "queued") return <span className="status status--pending">queued</span>;
  if (shot.state === "preparing") return <span className="status status--pending">preparing…</span>;
  if (shot.state === "uploading") return <span className="status status--pending">uploading…</span>;
  if (shot.state === "enriching") {
    return (
      <span className="status status--pending">
        {shot.status === "pending" || !shot.status ? "enriching…" : shot.status}
      </span>
    );
  }
  // settled
  if (shot.status === "needs_review") {
    return <span className="status status--ok">needs review ✓</span>;
  }
  return <span className="status status--ok">{shot.status ?? "done"}</span>;
}
